import styled, { css } from "styled-components";

// components + styles
import Button from "core/Button";
import Flexbox from "core/Flexbox";
import Messages from "core/Messages";
import BodyText from "core/BodyText";
import SectionContainer from "core/SectionContainer";
import RadioInput from "core/RadioInput";
import colors from "core/colors";
import { regular as regularFont } from "core/fontStyles";
import { focusOutline } from "core/links";
import { rapidEasing } from "core/animation";
import { spacingWithBreakpoints } from "core/spacing";
import {
    BlueBorderBottomLink,
    BlueBorderBottomLinkStyle
} from "core/Typography";
import { LabelContainer } from "core/Form/styles";
import breakpoints from "core/breakpoints";
import { textSm } from "core/fontStyles";

// helpers + external constants
import { convertPxToRem } from "frontend/components/helpers";
import { desktopHeaderHeight } from "core/SiteHeader/constants";

// constants:
const FOOTER_BOX_SHADOW = `0px -${convertPxToRem({ px: 4 })} ${convertPxToRem({
    px: 4
})} rgba(0, 0, 0, 0.15)`;

const FOOTER_HEIGHT_DESKTOP = 128;
const FOOTER_HEIGHT_TABLET = 120;

// styled components
export const SmallBlueTextLink = styled.a`
    ${BlueBorderBottomLinkStyle}
    ${textSm}
    display: inline-block;
`;

export const SmallBlueTextButton = styled.button`
    ${BlueBorderBottomLinkStyle}
    ${textSm}
    display: inline-block;
    cursor: pointer;
    background: transparent;
    border-top: 0;
    border-right: 0;
    border-left: 0;
    padding-top: 0;
    padding-right: 0;
    padding-left: 0;
`;

export const UploadMoreButton = styled(SmallBlueTextButton)`
    margin-left: ${convertPxToRem({ px: 8 })};
`;

export const Italic = styled.span`
    font-style: italic;
`;

export const UploadsSectionContainer = styled(SectionContainer)`
    ${({ hasHeaderHeight }) => css`
        /* Tablet - for sticky footer: */
        @media (min-width: ${breakpoints.tablet
                .rem}) and (max-width: ${breakpoints.beforeDesktop.rem}) {
            min-height: calc(
                100vh -
                    ${hasHeaderHeight
                        ? convertPxToRem({ px: hasHeaderHeight })
                        : convertPxToRem({ px: desktopHeaderHeight })} -
                    ${convertPxToRem({ px: FOOTER_HEIGHT_TABLET })}
            );
        }

        /* Desktop - for sticky footer: */
        min-height: calc(
            100vh -
                ${hasHeaderHeight
                    ? convertPxToRem({ px: hasHeaderHeight })
                    : convertPxToRem({ px: desktopHeaderHeight })} -
                ${convertPxToRem({ px: FOOTER_HEIGHT_DESKTOP })}
        );
    `}
`;

export const HeaderFlexbox = styled(Flexbox)`
    margin-bottom: ${convertPxToRem({ px: 16 })};
`;

export const MainHeadingContainer = styled.div`
    height: ${convertPxToRem({ px: 35 })};
`;

export const TypeFlexbox = styled(Flexbox)``;

export const NameFlexbox = styled(Flexbox)`
    margin-top: ${convertPxToRem({ px: 8 })};
    height: ${convertPxToRem({ px: 24 })};
`;

export const UploadedFilesControlsFlexbox = styled(Flexbox)``;

export const RadioFieldset = styled.fieldset`
    display: flex;
    margin-right: ${convertPxToRem({ px: 24 })};
`;

export const RadioLabel = styled.label`
    ${regularFont}
    display: flex;
    font-size: ${convertPxToRem({ px: 16 })};
    line-height: ${convertPxToRem({ px: 24 })};
    color: ${colors.secondary.orca};
    margin-right: ${convertPxToRem({ px: 16 })};

    &:last-child {
        margin-right: 0;
    }

    ${RadioInput} {
        margin-right: ${convertPxToRem({ px: 8 })};
    }
`;

export const ShootTitleBodyText = styled(BodyText)``;

export const ShootNameBodyText = styled(BodyText)`
    margin-left: ${convertPxToRem({ px: 4 })};
    font-style: italic;
`;

export const EditShootNameButton = styled.button`
    cursor: pointer;
    background: transparent;
    margin: 0 0 0 ${convertPxToRem({ px: 8 })};
    padding: 0;
    border: 0;
    font-size: 0;
    line-height: 0;
    margin: 0 0 0 ${convertPxToRem({ px: 8 })};

    svg {
        transition: all ${rapidEasing};

        path {
            fill: ${colors.accent.lightMode.persian};
        }
    }

    &:hover {
        svg path {
            fill: ${colors.secondary.oolong};
        }
    }

    // only show focus on keyboard not with mouse:
    &:focus:not(.focus-visible) {
        outline: none;
        box-shadow: none;
        border: none;
    }

    &:focus {
        ${focusOutline}
    }
`;

export const DropzoneContainer = styled.div`
    ${spacingWithBreakpoints.XL2Top}
`;

// sticky footer:
export const FooterSectionContainer = styled(SectionContainer)`
    position: relative;
    box-shadow: ${FOOTER_BOX_SHADOW};
    height: ${convertPxToRem({ px: FOOTER_HEIGHT_DESKTOP })};

    /* Tablet: */
    @media (min-width: ${breakpoints.tablet.rem}) and (max-width: ${breakpoints
            .beforeDesktop.rem}) {
        height: ${convertPxToRem({ px: FOOTER_HEIGHT_TABLET })};
    }
`;

export const FooterFlexbox = styled(Flexbox)``;

// shoot name change modal:
export const NameModalCancelButton = styled(BlueBorderBottomLink).attrs({
    as: "button"
})`
    cursor: pointer;
    margin: 0 0 0 ${convertPxToRem({ px: 24 })};
    background: transparent;
    border-top: 0;
    border-right: 0;
    border-left: 0;
    padding-top: 0;
    padding-right: 0;
    padding-left: 0;
`;

export const ModalChildrenContainer = styled.div`
    ${LabelContainer} {
        // hide label from UI but show to screen readers:
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
        //
    }
`;

// DroppedFileErrors:
export const ErrorContainer = styled.div`
    width: 75%;
    position: absolute;
`;

export const ErrorBanner = styled(Messages)`
    margin-bottom: ${convertPxToRem({ px: 24 })};

    &:last-of-type {
        margin-bottom: 0;
    }
`;

// when files have been uploaded - controls:
export const SelectedPhotosControlsFlexbox = styled(Flexbox)`
    ${spacingWithBreakpoints.XL2Top}
`;

export const SelectedPhotosButton = styled(Button)`
    margin-left: ${convertPxToRem({ px: 24 })};
`;
